import { Document, Page, pdfjs } from 'react-pdf';
import { useEffect, useRef, useState } from 'react';
import { Trans, useApplicationContext } from '~/common/context';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { CenteredSpinner, ErrorMessage, Flex, IconButton } from '@wyz/ui';
import { BlobHelpers, useBoolean } from '@wyz/utils';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`,
).toString();

export type PdfViewerProps = {
  url: string;
};

function PdfViewer({ url }: PdfViewerProps) {
  const { auth } = useApplicationContext();
  const [blob, setBlob] = useState<Blob | null>(null);
  const [pages, setPages] = useState<number>(0);
  const [filename, setFilename] = useState<string>('download.pdf');
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isError, errorHandlers] = useBoolean(false);
  const [isDownloadable, downloadableHandlers] = useBoolean(false);

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch(url, {
      headers: {
        ...(auth.user?.access_token
          ? { Authorization: `Bearer ${auth.user.access_token}` }
          : {}),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const contentDisposition = response.headers.get('Content-Disposition');
        setFilename(
          BlobHelpers.extractFilenameFromContentDisposition(
            contentDisposition,
            filename,
          ),
        );
        return response.blob();
      })
      .then((blob) => setBlob(blob))
      .catch(errorHandlers.setTrue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handleDownload = () => {
    if (!blob) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <div>
      <Flex justifyContent='end' className='mb-3'>
        <IconButton
          id='download-pdf'
          variant='primary'
          icon='download'
          onClick={handleDownload}
          disabled={!isDownloadable}
        >
          <Trans i18nKey='app.common.buttons.download' />
        </IconButton>
      </Flex>
      <div ref={pdfContainerRef}>
        {isError && (
          <ErrorMessage
            message={<Trans i18nKey='app.common.messages.error' />}
          />
        )}
        {blob && (
          <Document
            file={blob}
            onLoadSuccess={({ numPages }) => {
              downloadableHandlers.setTrue();
              setPages(numPages);
            }}
            loading={<CenteredSpinner />}
            error={
              <ErrorMessage
                message={<Trans i18nKey='app.common.messages.error' />}
              />
            }
          >
            {Array.from({ length: pages }, (_, page) => (
              <Page key={page} pageNumber={page + 1} width={containerWidth} />
            ))}
          </Document>
        )}
        {!blob && !isError && <CenteredSpinner />}
      </div>
    </div>
  );
}

export default PdfViewer;
